import { useState } from 'react'
import type { FamilyState, Member, Purchase, Reward } from '@domain/state.ts'
import type { RewardId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'
import { Pill } from './Pill.tsx'

/**
 * The shop, and the two things a child needs from it that are not "buy".
 *
 * The first is a way back. A purchase that no adult has handed over yet is a
 * request, not a transaction, so the child can undo their own change of mind
 * without asking. Once it has been granted the coins are genuinely spent, and
 * that line is where a price starts meaning something.
 *
 * The second is a way to wonder. "Lo que estoy pensando" is a list that costs
 * nothing: things go in and out of it, and it says what the lot would cost and
 * what would be left. It is a calculator with names on it, and it is where the
 * arithmetic of saving up actually gets learned — not by spending wrong and
 * regretting it, but by seeing the number before deciding.
 *
 * Buying still happens one thing at a time, on purpose. A "buy it all" button
 * would turn a list of wishes into a checkout, and half of it could be refused
 * by the day's screen cap, leaving a child holding a result nobody meant.
 */
export function Shop({
  member,
  state,
  day,
  onBuy,
  onUndo,
  onBack,
}: {
  member: Member
  state: FamilyState
  day: string
  onBuy: (reward: Reward) => Promise<string | null>
  onUndo: (purchase: Purchase) => Promise<string | null>
  onBack: () => void
}) {
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Only in this screen, only in this head. Nothing here is an event, because
  // nothing here has happened.
  const [thinking, setThinking] = useState<ReadonlySet<RewardId>>(new Set())

  const rewards = [...state.rewards.values()]
    .filter((r) => r.active && r.memberIds.includes(member.id))
    .sort((a, b) => a.price - b.price)

  // Not filtered by day, and that is the point: a purchase nobody resolved does
  // not stop existing because the clock passed four in the morning. The coins
  // are still spent, so the thing has to still be on the screen.
  const pending = [...state.purchases.values()].filter(
    (p) => p.memberId === member.id && p.status === 'pending',
  )
  const waiting = [...state.purchases.values()].filter(
    (p) => p.memberId === member.id && p.day === day && p.status === 'granted',
  )

  const planned = rewards.filter((r) => thinking.has(r.id))
  const planCost = planned.reduce((sum, r) => sum + r.price, 0)

  const toggleThinking = (id: RewardId): void => {
    setThinking((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const buy = async (reward: Reward): Promise<void> => {
    setBusy(reward.id)
    try {
      const error = await onBuy(reward)
      setMessage(error ?? es.shop.bought)
      // Bought means decided, so it leaves the thinking list either way.
      if (!error) toggleThinkingOff(reward.id)
    } finally {
      setBusy(null)
    }
  }

  const toggleThinkingOff = (id: RewardId): void => {
    setThinking((current) => {
      if (!current.has(id)) return current
      const next = new Set(current)
      next.delete(id)
      return next
    })
  }

  const undo = async (purchase: Purchase): Promise<void> => {
    setBusy(purchase.id)
    try {
      const error = await onUndo(purchase)
      setMessage(error ?? es.shop.undone)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-12">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>

      <h1 className="mb-1 text-3xl font-extrabold tracking-tight">🛒 {es.shop.title}</h1>
      <p className="text-gold mt-0 mb-6 text-lg font-extrabold">
        {es.shop.balance(member.coins, member.coinWord ?? es.today.coins)}
      </p>

      {message ? (
        <p role="status" className="bg-teal-soft text-teal mb-5 rounded-lg p-3 font-bold">
          {message}
        </p>
      ) : null}

      {planned.length > 0 ? (
        <section className="border-teal bg-teal-soft mb-6 rounded-xl border-2 p-4">
          <h2 className="m-0 mb-1 text-lg font-extrabold">{es.shop.planTitle}</h2>
          <p className="text-muted m-0 mb-3 text-sm">{es.shop.planHelp}</p>
          <ul className="m-0 mb-3 grid list-none gap-1 p-0">
            {planned.map((reward) => (
              <li key={reward.id} className="m-0 flex items-baseline justify-between gap-3">
                <span className="font-bold">{reward.name}</span>
                <span className="text-gold shrink-0 font-extrabold">{reward.price}</span>
              </li>
            ))}
          </ul>
          <p className="m-0 font-extrabold">{es.shop.planCost(planCost)}</p>
          <p className="m-0 mt-1 font-bold">
            {planCost <= member.coins
              ? es.shop.planLeft(member.coins - planCost)
              : es.shop.planMissing(planCost - member.coins)}
          </p>
          <button
            type="button"
            onClick={() => {
              setThinking(new Set())
            }}
            className="text-teal mt-3 text-sm font-bold underline underline-offset-4"
          >
            {es.shop.planClear}
          </button>
        </section>
      ) : null}

      {rewards.length === 0 ? (
        <p className="text-muted">{es.shop.empty}</p>
      ) : (
        <ul className="grid list-none gap-3 p-0">
          {rewards.map((reward) => {
            const affordable = reward.price <= member.coins
            const inPlan = thinking.has(reward.id)
            return (
              <li key={reward.id} className="border-rule bg-surface m-0 rounded-xl border p-4">
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-lg font-bold">{reward.name}</p>
                    {reward.notes ? (
                      <p className="text-muted m-0 mt-1 text-sm">{reward.notes}</p>
                    ) : null}
                    {affordable ? (
                      <p className="text-muted m-0 mt-1 text-sm">
                        {es.shop.left(member.coins - reward.price)}
                      </p>
                    ) : null}
                  </div>
                  <span className="text-gold shrink-0 text-xl font-extrabold">{reward.price}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={!affordable || busy === reward.id}
                    onClick={() => {
                      void buy(reward)
                    }}
                    className="bg-teal flex-1 rounded-lg px-6 py-3 font-bold text-white disabled:opacity-40"
                  >
                    {affordable ? es.shop.buy : es.shop.missing(reward.price - member.coins)}
                  </button>
                  <button
                    type="button"
                    aria-pressed={inPlan}
                    onClick={() => {
                      toggleThinking(reward.id)
                    }}
                    className={`border-rule rounded-lg border-2 px-4 py-3 text-sm font-bold ${
                      inPlan ? 'bg-teal-soft text-teal border-teal' : ''
                    }`}
                  >
                    {inPlan ? es.shop.unthink : es.shop.think}
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {pending.length > 0 ? (
        <section className="mt-8">
          <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
            {es.shop.pendingTitle}
          </h2>
          <ul className="grid list-none gap-2 p-0">
            {pending.map((purchase) => (
              <li
                key={purchase.id}
                className="border-rule bg-surface m-0 flex flex-wrap items-center gap-3 rounded-xl border p-3"
              >
                <span className="min-w-0 flex-1 font-bold">
                  {state.rewards.get(purchase.rewardId)?.name ?? ''}
                </span>
                <span className="text-gold shrink-0 font-extrabold">
                  {purchase.price * purchase.quantity}
                </span>
                <button
                  type="button"
                  disabled={busy === purchase.id}
                  onClick={() => {
                    void undo(purchase)
                  }}
                  className="border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold disabled:opacity-40"
                >
                  {es.shop.undo}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {waiting.length > 0 ? (
        <section className="mt-6">
          <ul className="grid list-none gap-2 p-0">
            {waiting.map((purchase) => (
              <li key={purchase.id} className="m-0">
                <Pill tone="done">{state.rewards.get(purchase.rewardId)?.name ?? ''}</Pill>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
